import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useGetWorkplaceQuiz } from "@src/appV2/WorkplaceQuiz/api/useGetWorkplaceQuiz/useGetWorkplaceQuiz";
import { type GetWorkplaceQuizResponse } from "@src/appV2/WorkplaceQuiz/types";
import { type UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";

import { type FacilityNote, type FacilityRule } from "../types";

export type WorkplaceRulesResponse = FacilityRule[];

// Type guard to narrow included item type to FacilityNote
const FACILITY_NOTE_TYPE = "FacilityNote";
function isFacilityNote<T extends Record<string, unknown>>(
  value: T
): value is T & { type: typeof FACILITY_NOTE_TYPE; id: string; attributes: FacilityNote } {
  return value.type === FACILITY_NOTE_TYPE;
}

export function useWorkplaceRules(
  workplaceId: string,
  options: UseGetQueryOptions<GetWorkplaceQuizResponse> = {}
): Pick<UseQueryResult<WorkplaceRulesResponse>, "data" | "isLoading"> {
  const { data, ...rest } = useGetWorkplaceQuiz(workplaceId, options);

  const rules = useMemo(() => {
    const collected: FacilityRule[] = [];

    if (isDefined(data?.included)) {
      for (const item of data.included) {
        if (isFacilityNote(item)) {
          collected.push(...(item.attributes.rules ?? []));
        }
      }
    }

    return collected;
  }, [data]);

  return {
    data: rules,
    ...rest,
  };
}
